/**
 * Dialog360 Config Panel
 * Read-only view of the EFFECTIVE 360dialog config currently in force (DB override where set,
 * otherwise appsettings). Lets admins see what's actually configured on the Settings page —
 * secret values are masked (the API key shows only a tail + "Configured").
 */

import { useEffect, type ReactNode } from 'react';
import { IconRefresh } from '@tabler/icons-react';
import { Stack, Group, Text, Title, Badge, Button, Loader } from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import { useDialog360 } from '../hooks/useDialog360';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group justify="space-between" gap="sm" wrap="nowrap" align="flex-start">
      <Text size="sm" c="dimmed">{label}</Text>
      <div style={{ textAlign: 'right', minWidth: 0 }}>{children}</div>
    </Group>
  );
}

export function Dialog360ConfigPanel() {
  const { loading, config, fetchConfig } = useDialog360();

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  return (
    <Card withBorder padding="lg">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={5}>Current configuration</Title>
            <Text size="xs" c="dimmed">
              Effective values in force now — DB override where set, otherwise appsettings. Secrets are masked.
            </Text>
          </div>
          <Button variant="light" size="xs" leftSection={<IconRefresh size={14} />} onClick={() => fetchConfig()} loading={loading}>
            Refresh
          </Button>
        </Group>

        {loading && !config ? (
          <Group justify="center" p="md"><Loader /></Group>
        ) : config ? (
          <Stack gap={8}>
            <Row label="WhatsApp Business Account ID">
              {config.wabaId
                ? <Text size="sm" ff="monospace">{config.wabaId}</Text>
                : <Badge size="sm" variant="light" color="orange">Not set</Badge>}
            </Row>
            <Row label="Phone number">
              <Text size="sm" ff="monospace">{config.phoneNumberId || '—'}</Text>
            </Row>
            <Row label="API URL">
              <Text size="sm" ff="monospace">{config.apiUrl || '—'}</Text>
            </Row>
            <Row label="API key">
              {config.apiKeySet ? (
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <Text size="sm" ff="monospace">{config.apiKeyMasked}</Text>
                  <Badge size="sm" variant="light" color="green">Configured</Badge>
                </Group>
              ) : (
                <Badge size="sm" variant="light" color="orange">Not set</Badge>
              )}
            </Row>
            <Row label="Billing currency">
              <Text size="sm">{config.billingCurrency || '—'}</Text>
            </Row>
            <Row label="Webhook secret">
              <Badge size="sm" variant="light" color={config.webhookSecretSet ? 'green' : 'gray'}>
                {config.webhookSecretSet ? 'Configured' : 'Not set'}
              </Badge>
            </Row>
            <Row label="Verify token">
              <Badge size="sm" variant="light" color={config.webhookVerifyTokenSet ? 'green' : 'gray'}>
                {config.webhookVerifyTokenSet ? 'Configured' : 'Not set'}
              </Badge>
            </Row>
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">Could not load current configuration.</Text>
        )}
      </Stack>
    </Card>
  );
}
